import { useEffect, useRef, useState } from 'react'

import {
  applyPortalTheme,
  DEFAULT_MEMBERS_PORTAL_ACCENT,
  DEFAULT_MEMBERS_PORTAL_THEME_MODE,
  resolveHeroOverlayOpacity,
} from '../theme'

const MAX_SSE_BACKOFF_MS = 60_000

function resolveThemePayload(payload = {}, clubCode) {
  const nestedTheme = payload?.theme && typeof payload.theme === 'object' ? payload.theme : null

  return {
    clubCode,
    accentColor: nestedTheme?.accentColor || payload?.accentColor || DEFAULT_MEMBERS_PORTAL_ACCENT,
    themeMode: nestedTheme?.themeMode || payload?.themeMode || DEFAULT_MEMBERS_PORTAL_THEME_MODE,
    heroOverlayOpacity: resolveHeroOverlayOpacity(nestedTheme?.heroOverlayOpacity ?? payload?.heroOverlayOpacity),
    updatedAt: nestedTheme?.updatedAt || payload?.updatedAt || null,
  }
}

function applyHeroOverlayCssVar(heroOverlayOpacity) {
  if (typeof document === 'undefined') return
  const resolved = resolveHeroOverlayOpacity(heroOverlayOpacity)
  document.documentElement.style.setProperty('--mp-hero-overlay-opacity', String(Number(resolved.toFixed(2))))
}

function updateThemeMetaColor(accentColor) {
  if (typeof document === 'undefined') return
  const metaThemeColor = document.querySelector('meta[name="theme-color"]')
  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', accentColor)
  }
}

export function useLivePortalTheme({
  clubCode,
  enabled = true,
  initialAccentColor,
  initialThemeMode,
  initialHeroOverlayOpacity,
  initialUpdatedAt,
} = {}) {
  const [liveThemeVersion, setLiveThemeVersion] = useState(initialUpdatedAt || null)
  const [sseHealthy, setSseHealthy] = useState(false)
  const reconnectTimeoutRef = useRef(null)
  const eventSourceRef = useRef(null)
  const failureCountRef = useRef(0)
  const lastErrorTypeRef = useRef(null)
  const latestThemeRef = useRef({
    accentColor: initialAccentColor || DEFAULT_MEMBERS_PORTAL_ACCENT,
    themeMode: initialThemeMode || DEFAULT_MEMBERS_PORTAL_THEME_MODE,
    heroOverlayOpacity: resolveHeroOverlayOpacity(initialHeroOverlayOpacity),
    updatedAt: initialUpdatedAt || null,
  })

  useEffect(() => {
    latestThemeRef.current = {
      accentColor: initialAccentColor || latestThemeRef.current.accentColor,
      themeMode: initialThemeMode || latestThemeRef.current.themeMode,
      heroOverlayOpacity: resolveHeroOverlayOpacity(initialHeroOverlayOpacity ?? latestThemeRef.current.heroOverlayOpacity),
      updatedAt: initialUpdatedAt || latestThemeRef.current.updatedAt,
    }
  }, [initialAccentColor, initialThemeMode, initialHeroOverlayOpacity, initialUpdatedAt])

  useEffect(() => {
    if (!enabled || !clubCode || typeof window === 'undefined') return undefined
    let disposed = false

    const initialAccent = initialAccentColor || DEFAULT_MEMBERS_PORTAL_ACCENT
    const initialMode = initialThemeMode || DEFAULT_MEMBERS_PORTAL_THEME_MODE
    applyPortalTheme({ accentColor: initialAccent, themeMode: initialMode })
    applyHeroOverlayCssVar(resolveHeroOverlayOpacity(latestThemeRef.current.heroOverlayOpacity))
    updateThemeMetaColor(initialAccent)

    const cleanup = () => {
      if (reconnectTimeoutRef.current) {
        window.clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
    }

    const applyPayload = (rawPayload) => {
      const payload = resolveThemePayload(rawPayload, clubCode)
      const unchanged =
        latestThemeRef.current.updatedAt === payload.updatedAt
        && latestThemeRef.current.accentColor === payload.accentColor
        && latestThemeRef.current.themeMode === payload.themeMode
        && latestThemeRef.current.heroOverlayOpacity === payload.heroOverlayOpacity

      if (unchanged) return

      latestThemeRef.current = {
        accentColor: payload.accentColor,
        themeMode: payload.themeMode,
        heroOverlayOpacity: payload.heroOverlayOpacity,
        updatedAt: payload.updatedAt,
      }

      applyPortalTheme({ accentColor: payload.accentColor, themeMode: payload.themeMode })
      applyHeroOverlayCssVar(payload.heroOverlayOpacity)
      updateThemeMetaColor(payload.accentColor)
      setLiveThemeVersion(payload.updatedAt)
    }

    const scheduleReconnect = () => {
      if (disposed) return
      const backoffMs = Math.min(MAX_SSE_BACKOFF_MS, 1000 * 2 ** Math.max(0, failureCountRef.current - 1))
      reconnectTimeoutRef.current = window.setTimeout(connect, backoffMs)
    }

    const handleIncoming = (event) => {
      try {
        const parsed = JSON.parse(event.data)
        applyPayload(parsed)
        failureCountRef.current = 0
        setSseHealthy(true)
        lastErrorTypeRef.current = null
      } catch {
        // ignore malformed server payload
      }
    }

    const connect = () => {
      cleanup()

      if (typeof window.EventSource !== 'function') {
        lastErrorTypeRef.current = 'unsupported'
        setSseHealthy(false)
        return
      }

      const streamUrl = `/api/members-portal/${clubCode}/theme/stream`
      const source = new window.EventSource(streamUrl)
      eventSourceRef.current = source

      source.addEventListener('hello', handleIncoming)
      source.addEventListener('theme', handleIncoming)
      source.onmessage = handleIncoming
      source.onerror = () => {
        if (disposed) return
        failureCountRef.current += 1
        lastErrorTypeRef.current = 'transport'
        setSseHealthy(false)
        if (eventSourceRef.current) {
          eventSourceRef.current.close()
          eventSourceRef.current = null
        }
        scheduleReconnect()
      }
    }

    connect()

    return () => {
      disposed = true
      cleanup()
    }
  }, [clubCode, enabled, initialAccentColor, initialThemeMode])

  return { liveThemeVersion, sseHealthy, lastErrorType: lastErrorTypeRef.current }
}
